import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocalizedLabel, getLocalizedDescription } from '@/lib/localization-helpers'
import { useQuery } from '@tanstack/react-query'
import { Main } from '@/components/layout/main'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { get_cabinet_template, render_cabinet_template, update_cabinet_template, delete_cabinet_template, preview_pdf_cabinet_template } from '@/api/document-templates-cabinet'
import { get_cases } from '@/api/case'
import type { DocumentTemplate } from '@/types/document-template'
import { useToast } from '@/hooks/use-toast'
import { PermissionGuard } from '@/components/permission-guard'
import { UserPermission } from '@/constants/permissions'
import { FileText, Loader2, ArrowLeft, ExternalLink, Edit, Save, X, Trash2, Eye } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'

export const Route = createFileRoute('/_authenticated/cabinet-document-template/$slug')({
  component: Page,
})

// Helper to get field value from flat key payload
const getFieldValue = (payload: Record<string, any>, path: string) => {
  return payload[path] ?? ''
}


interface FieldInputProps {
  field: any
  value: any
  onChange: (value: any) => void
}

interface VariableInputProps {
  variable: any
  value: any
  onChange: (value: any) => void
  language?: string
}

interface VariableInputProps {
  variable: any
  value: any
  onChange: (value: any) => void
}

const FieldInput = ({ field, value, onChange }: FieldInputProps) => {
  const { t } = useTranslation()
  const type = field.type || 'string'
  const format = field.format

  const getInputType = () => {
    if (format === 'date') return 'date'
    if (format === 'datetime') return 'datetime-local'
    if (format === 'email') return 'email'
    if (format === 'phone' || format === 'tel') return 'tel'
    if (type === 'number' || format === 'number') return 'number'
    return 'text'
  }

  const getPlaceholder = () => {
    if (field.example) return field.example
    if (format === 'date') return 'YYYY-MM-DD'
    if (format === 'email') return 'exemple@email.com'
    if (format === 'phone') return '+212 6XX XXX XXX'
    if (type === 'number') return '0'
    return `${t('enter') || 'Enter'} ${field.title || field.path}`
  }

  if (type === 'boolean') {
    return (
      <div className='flex items-center gap-2'>
        <input
          type='checkbox'
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className='h-4 w-4 rounded border-gray-300'
        />
      </div>
    )
  }

  if (format === 'long_text') {
    return (
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={getPlaceholder()}
        rows={4}
      />
    )
  }

  return (
    <Input
      type={getInputType()}
      value={value || ''}
      onChange={(e) => {
        const inputType = getInputType()
        onChange(inputType === 'number' ? Number(e.target.value) : e.target.value)
      }}
      placeholder={getPlaceholder()}
    />
  )
}

const VariableInput = ({ variable, value, onChange, language = 'en' }: VariableInputProps) => {
  const getInputType = (): string => {
    if (variable.type === 'date') return 'date'
    if (variable.type === 'datetime') return 'datetime-local'
    if (variable.type === 'number' || variable.type === 'integer') return 'number'
    return 'text'
  }

  if (variable.type === 'boolean') {
    return (
      <div className='flex items-center gap-2'>
        <input
          type='checkbox'
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className='h-4 w-4 rounded border-gray-300'
        />
      </div>
    )
  }

  return (
    <Input
      type={getInputType()}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={variable.example || getLocalizedLabel(variable, language)}
    />
  )
}

function Page() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { slug } = useParams({ from: '/_authenticated/cabinet-document-template/$slug' })
  const { toast } = useToast()

  const [tpl, setTpl] = useState<DocumentTemplate | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [payload, setPayload] = useState<Record<string, any>>({})
  const [rendering, setRendering] = useState<boolean>(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [selectedCaseId, setSelectedCaseId] = useState<string | undefined>(undefined)
  const [caseSearchQuery, setCaseSearchQuery] = useState<string>('')
  const [loadingContext, setLoadingContext] = useState<boolean>(false)
  const [variables, setVariables] = useState<any>(null)

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editData, setEditData] = useState({ title: '', is_active: false, html_body: '' })
  const quillRef = useRef<Quill | null>(null)
  const editorContainerRef = useRef<HTMLDivElement | null>(null)

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Preview PDF state
  const [generatingPreview, setGeneratingPreview] = useState(false)

  const currentLanguage = i18n.language || 'en'

  const fields = useMemo(() => tpl?.form_fields ?? [], [tpl])
  
  // Check if we have content to render (variables or fields)
  const hasContent = useMemo(() => {
    const hasVars = variables && ((variables.system?.length || 0) + (variables.custom?.length || 0) > 0)
    const hasFields = fields.length > 0
    return hasVars || hasFields
  }, [variables, fields])

  // Fetch cases with search
  const { data: casesData, isPending: casesLoading } = useQuery({
    queryKey: ['cabinet-cases', caseSearchQuery],
    queryFn: async () => {
      const result = await get_cases({
        page: 0,
        page_size: 20,
        ordering: '-created_at',
        name: caseSearchQuery || undefined,
        client_id: undefined
      })
      return result
    },
    staleTime: 60000
  })

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await get_cabinet_template(slug)
        setTpl(data)
        setVariables(data.variables)
        initializePayload(data.variables)
        setEditData({
          title: data.title,
          is_active: data.is_active,
          html_body: data.engine === 'html' ? (data.html_body || '') : '',
        })
      } catch (e: any) {
        setError(e?.message || t('error_loading') || 'Failed to load template')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [slug, t])

  const initializePayload = (vars: any) => {
    if (!vars) return
    const newPayload: Record<string, any> = {}
    
    // Handle both structures: system/custom directly OR categorized.system/custom
    const systemVars = vars.system || (vars.categorized?.system || [])
    const customVars = vars.custom || (vars.categorized?.custom || [])
    
    systemVars.forEach((variable: any) => {
      if (variable.value !== null && variable.value !== undefined) {
        newPayload[variable.key] = variable.value
      }
    })
    
    customVars.forEach((variable: any) => {
      if (variable.value !== null && variable.value !== undefined) {
        newPayload[variable.key] = variable.value
      }
    })
    
    setPayload(newPayload)
  }

  // When case is selected, reload template with context
  useEffect(() => {
    if (!selectedCaseId || !slug) return

    const loadTemplateWithContext = async () => {
      setLoadingContext(true)
      try {
        const data = await get_cabinet_template(slug, { case_id: selectedCaseId })
        setTpl(data)
        setVariables(data.variables)
        initializePayload(data.variables)
      } catch (e: any) {
        toast({
          title: t('error') || 'Error',
          description: e?.message || t('failed_load_case_context') || 'Failed to load template with case context',
          variant: 'destructive'
        })
      } finally {
        setLoadingContext(false)
      }
    }

    loadTemplateWithContext()
  }, [selectedCaseId, slug, t, toast])

  const onFieldChange = (path: string, value: any) => {
    setPayload((old) => ({
      ...old,
      [path]: value
    }))
  }

  const onVariableChange = (key: string, value: any) => {
    setPayload((old) => ({
      ...old,
      [key]: value
    }))
  }

  const convertPayloadTypes = (payload: Record<string, any>) => {
    const converted: Record<string, any> = {}
    
    // Process form fields
    fields.forEach((field) => {
      const value = payload[field.path]
      
      // Skip if value is undefined or null or empty string
      if (value === undefined || value === null || value === '') return
      
      const type = field.type || 'string'
      const format = field.format
      
      // Convert based on type
      if (type === 'number' || format === 'number') {
        const numValue = Number(value)
        if (!isNaN(numValue)) {
          converted[field.path] = numValue
        } else {
          converted[field.path] = value
        }
      } else if (type === 'integer') {
        const intValue = parseInt(String(value), 10)
        if (!isNaN(intValue)) {
          converted[field.path] = intValue
        } else {
          converted[field.path] = value
        }
      } else if (type === 'boolean') {
        if (typeof value === 'boolean') {
          converted[field.path] = value
        } else if (typeof value === 'string') {
          converted[field.path] = value.toLowerCase() === 'true' || value === '1'
        } else {
          converted[field.path] = Boolean(value)
        }
      } else if (type === 'array') {
        converted[field.path] = Array.isArray(value) ? value : [value]
      } else {
        // Keep as string or original value
        converted[field.path] = String(value)
      }
    })
    
    // Process variables (system and custom)
    if (variables) {
      const allVariables = [...(variables.system || []), ...(variables.custom || [])]
      allVariables.forEach((variable: any) => {
        const value = payload[variable.key]
        
        // Include all non-empty values for variables
        if (value !== undefined && value !== null && value !== '') {
          const type = variable.type || 'string'
          
          if (type === 'number' || type === 'integer') {
            const numValue = Number(value)
            converted[variable.key] = !isNaN(numValue) ? numValue : value
          } else if (type === 'boolean') {
            converted[variable.key] = typeof value === 'boolean' ? value : Boolean(value)
          } else {
            converted[variable.key] = String(value)
          }
        }
      })
    }
    
    return converted
  }

  const handleRender = async () => {
    if (!tpl?.slug) return
    setRendering(true)
    setPdfUrl(null)
    try {
      const typedPayload = convertPayloadTypes(payload)
      const res = await render_cabinet_template(tpl.slug, typedPayload)
      const pdfUrl = (res as any).pdf_url || res.document?.file_path
      if (pdfUrl) {
        setPdfUrl(pdfUrl)
        window.open(pdfUrl, '_blank')
        toast({ title: t('success') || 'Success', description: t('render_success') || 'Document rendered successfully' })
      }
    } catch (e: any) {
      toast({ title: t('error') || 'Error', description: e?.message || t('render_failed') || 'Failed to render document', variant: 'destructive' })
    } finally {
      setRendering(false)
    }
  }

  // Initialize Quill for edit mode
  useEffect(() => {
    if (isEditMode && tpl?.engine === 'html' && editorContainerRef.current && !quillRef.current) {
      const quill = new Quill(editorContainerRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['blockquote', 'code-block'],
            ['link', 'image'],
            ['clean'],
          ],
        },
      })
      quill.root.innerHTML = editData.html_body
      quillRef.current = quill
      quill.on('text-change', () => {
        setEditData(prev => ({ ...prev, html_body: quill.root.innerHTML }))
      })
    }
    return () => {
      if (!isEditMode) {
        quillRef.current = null
      }
    }
  }, [isEditMode, tpl])

  const handleSaveTemplate = async () => {
    if (!editData.title.trim()) {
      toast({ title: t('error') || 'Error', description: t('title_required') || 'Title is required', variant: 'destructive' })
      return
    }
    setIsSaving(true)
    try {
      const updatePayload: any = { title: editData.title, is_active: editData.is_active }
      if (tpl?.engine === 'html' && editData.html_body) {
        updatePayload.html_body = editData.html_body
      }
      const updated = await update_cabinet_template(tpl!.slug, updatePayload)
      setTpl(updated)
      setIsEditMode(false)
      toast({ title: t('template_updated') || 'Template Updated', description: t('template_saved_successfully') || 'Template has been saved successfully' })
    } catch (e: any) {
      toast({ title: t('error_updating_template') || 'Error Updating Template', description: e?.detail || e?.message || String(e), variant: 'destructive' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      await delete_cabinet_template(tpl!.slug)
      toast({ title: t('success'), description: t('template_deleted_successfully') || 'Template deleted successfully' })
      navigate({ to: '/cabinet-document-templates' })
    } catch (e: any) {
      toast({ title: t('error'), description: e?.detail || e?.message || String(e), variant: 'destructive' })
    }
  }

  const handlePreviewPdf = async () => {
    if (!tpl?.slug) return
    setGeneratingPreview(true)
    try {
      const result = await preview_pdf_cabinet_template(tpl.slug)
      if (result?.preview_pdf_url) {
        window.open(result.preview_pdf_url, '_blank')
        toast({ title: t('preview_generated') || 'Preview Generated', description: t('preview_pdf_url') || 'Preview PDF opened in new tab' })
      }
    } catch (e: any) {
      toast({ title: t('error') || 'Error', description: e?.detail || e?.message || t('preview_failed') || 'Failed to generate preview', variant: 'destructive' })
    } finally {
      setGeneratingPreview(false)
    }
  }

  if (loading) {
    return (
      <>

        <Main>
          <div className='space-y-4'><Skeleton className='h-10 w-64' /><Skeleton className='h-64 w-full' /><Skeleton className='h-96 w-full' /></div>
        </Main>
      </>
    )
  }

  if (error || !tpl) {
    return (
      <>

        <Main>
          <Card><CardContent className='p-8 text-center'><p className='text-destructive'>{error || t('template_not_found')}</p><Button variant='outline' className='mt-4' onClick={() => navigate({ to: '/cabinet-document-templates' })}><ArrowLeft className='mr-2 h-4 w-4' />{t('back') || 'Back'}</Button></CardContent></Card>
        </Main>
      </>
    )
  }

  return (
    <>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete') || 'Confirm Delete'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_template_confirmation') || 'Are you sure you want to delete this template?'}{' '}
              {tpl && <span className='font-semibold'>"{ tpl.title}"</span>}
              {'. '}{t('action_cannot_be_undone') || 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className='bg-destructive text-destructive-foreground hover:bg-destructive/90'>
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Main>
        <div className='mb-6'>
          <Button variant='ghost' size='sm' onClick={() => navigate({ to: '/cabinet-document-templates' })} className='mb-4'>
            <ArrowLeft className='mr-2 h-4 w-4' />{t('back_to_templates') || 'Back to Templates'}
          </Button>
          <div className='flex items-start justify-between gap-4'>
            <div>
              <h2 className='text-2xl font-bold tracking-tight flex items-center gap-2'><FileText className='h-6 w-6' />{tpl?.title}</h2>
              <p className='text-sm text-muted-foreground mt-1'>{t('version') || 'Version'}: {tpl?.version} • {t('engine') || 'Engine'}: {tpl?.engine?.toUpperCase() || 'HTML'}</p>
            </div>
            <div className='flex items-center gap-2'>
              <Badge variant={tpl?.is_active ? 'default' : 'secondary'}>
                {tpl?.is_active ? t('active') : t('inactive')}
              </Badge>
              <PermissionGuard permission={UserPermission.CHANGE_DOCUMENT_TEMPLATE}>
                {!isEditMode && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setEditData({ title: tpl?.title || '', is_active: tpl?.is_active ?? false, html_body: tpl?.engine === 'html' ? (tpl?.html_body || '') : '' })
                      setIsEditMode(true)
                    }}
                    className='gap-2'
                  >
                    <Edit className='w-4 h-4' />{t('edit') || 'Edit'}
                  </Button>
                )}
              </PermissionGuard>
              <PermissionGuard permission={UserPermission.CHANGE_DOCUMENT_TEMPLATE}>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handlePreviewPdf}
                  disabled={generatingPreview}
                  className='gap-2'
                >
                  {generatingPreview ? <Loader2 className='w-4 h-4 animate-spin' /> : <Eye className='w-4 h-4' />}
                  {t('preview_pdf') || 'Preview PDF'}
                </Button>
              </PermissionGuard>
              <PermissionGuard permission={UserPermission.DELETE_DOCUMENT_TEMPLATE}>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setDeleteDialogOpen(true)}
                  className='gap-2 text-destructive hover:bg-destructive/10'
                >
                  <Trash2 className='w-4 h-4' />{t('delete') || 'Delete'}
                </Button>
              </PermissionGuard>
            </div>
          </div>
        </div>

        {/* Edit Mode Panel */}
        {isEditMode && (
          <Card className='mb-6'>
            <CardHeader>
              <CardTitle>{t('edit_template') || 'Edit Template'}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='edit-title'>{t('title') || 'Title'} <span className='text-destructive'>*</span></Label>
                <Input
                  id='edit-title'
                  value={editData.title}
                  onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                  disabled={isSaving}
                />
              </div>
              <div className='flex items-center gap-2'>
                <input
                  id='edit-is-active'
                  type='checkbox'
                  checked={editData.is_active}
                  onChange={(e) => setEditData(prev => ({ ...prev, is_active: e.target.checked }))}
                  disabled={isSaving}
                  className='w-4 h-4 rounded'
                />
                <Label htmlFor='edit-is-active' className='font-normal cursor-pointer'>{t('active') || 'Active'}</Label>
              </div>
              {tpl?.engine === 'html' && (
                <div className='space-y-2'>
                  <Label>{t('template_content') || 'Template Content'}</Label>
                  <Card className='border'>
                    <CardContent className='p-0'>
                      <div ref={editorContainerRef} className='quill-editor' style={{ minHeight: '400px' }} />
                    </CardContent>
                  </Card>
                </div>
              )}
              <div className='flex gap-2 pt-2'>
                <Button onClick={handleSaveTemplate} disabled={isSaving} size='sm' className='gap-2'>
                  <Save className='w-4 h-4' />
                  {isSaving ? (t('saving') || 'Saving...') : (t('save_changes') || 'Save Changes')}
                </Button>
                <Button
                  onClick={() => { setIsEditMode(false); quillRef.current = null }}
                  disabled={isSaving}
                  variant='outline'
                  size='sm'
                  className='gap-2'
                >
                  <X className='w-4 h-4' />{t('cancel') || 'Cancel'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {tpl?.preview_url && (
          <Card className='mb-6'><CardHeader><CardTitle>{t('preview') || 'Preview'}</CardTitle></CardHeader><CardContent><img src={tpl.preview_url} alt={tpl.title} className='w-full h-auto rounded border object-contain max-h-96' /></CardContent></Card>
        )}

        <Separator className='my-6' />

        {/* Document Generation Section - only shown when not editing */}
        {!isEditMode && (
          <Card className='mb-6'>
            <CardHeader><CardTitle>{t('document_information') || 'Document Information'}</CardTitle><CardDescription>{t('fill_form_to_generate') || 'Fill out the form below to generate your document'}</CardDescription></CardHeader>
            <CardContent className='space-y-6'>
              {/* Select Case Section */}
              <div className='space-y-4 border-b pb-6'>
                <div>
                  <Label>{t('case') || 'Case'} ({t('optional') || 'Optional'})</Label>
                  <p className='text-xs text-muted-foreground mb-3'>{t('select_case_context') || 'Select a case to pre-fill template variables'}</p>
                  <div className='flex gap-2'>
                    <Input
                      placeholder={t('search_cases') || 'Search cases...'}
                      value={caseSearchQuery}
                      onChange={(e) => setCaseSearchQuery(e.target.value)}
                      className='flex-1'
                      disabled={loadingContext}
                    />
                  </div>
                  {casesLoading && <p className='text-xs text-muted-foreground mt-2'>{t('loading') || 'Loading cases...'}</p>}
                  {casesData?.results && casesData.results.length > 0 && (
                    <div className='mt-2 space-y-1 max-h-48 overflow-y-auto border rounded p-2'>
                      {casesData.results.map((caseItem: any) => (
                        <button
                          key={caseItem.id}
                          onClick={() => setSelectedCaseId(caseItem.id)}
                          disabled={loadingContext}
                          className={`w-full text-left px-3 py-2 rounded text-sm transition-colors disabled:opacity-50 ${
                            selectedCaseId === caseItem.id
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted'
                          }`}
                        >
                          {loadingContext && selectedCaseId === caseItem.id && <Loader2 className='inline mr-2 h-3 w-3 animate-spin' />}
                          {caseItem.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedCaseId && (
                    <div className='mt-2'>
                      <Badge variant='secondary'>
                        {casesData?.results?.find((c: any) => c.id === selectedCaseId)?.name || selectedCaseId}
                        <button
                          onClick={() => setSelectedCaseId(undefined)}
                          disabled={loadingContext}
                          className='ml-2 text-xs hover:text-destructive disabled:opacity-50'
                        >
                          ✕
                        </button>
                      </Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Template Variables Editable Section */}
              {variables && (variables.system?.length || 0) + (variables.custom?.length || 0) > 0 && (
                <div className='space-y-4 border-b pb-6'>
                  <Label className='text-sm font-semibold'>{t('template_variables') || 'Template Variables'}</Label>
                  <p className='text-xs text-muted-foreground'>{t('edit_template_variables') || 'Edit template variables to customize your document'}</p>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    {[...(variables.system || []), ...(variables.custom || [])].map((variable: any) => (
                      <div key={variable.key} className='space-y-2 p-3 border rounded bg-muted/20'>
                        <div className='flex items-start justify-between gap-2'>
                          <div>
                            <Label htmlFor={`var-${variable.key}`} className='text-sm'>{getLocalizedLabel(variable, currentLanguage)}</Label>
                            {getLocalizedDescription(variable, currentLanguage) && <p className='text-xs text-muted-foreground mt-1'>{getLocalizedDescription(variable, currentLanguage)}</p>}
                          </div>
                          {variable.required_context && <Badge variant='outline' className='text-xs shrink-0'>{variable.required_context}</Badge>}
                        </div>
                        <VariableInput
                          variable={variable}
                          value={payload[variable.key]}
                          onChange={(val) => onVariableChange(variable.key, val)}
                          language={currentLanguage}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Form Fields */}
              {fields.length > 0 && (
                <div className='space-y-4 border-b pb-6'>
                  <Label className='text-sm font-semibold'>{t('form_fields') || 'Form Fields'}</Label>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    {fields.map((field) => (
                      <div key={field.path} className='space-y-2'>
                        <Label htmlFor={field.path}>
                          {field.title || field.path}
                          {field.required && <span className='text-destructive ml-1'>*</span>}
                        </Label>
                        {field.description && <p className='text-xs text-muted-foreground'>{field.description}</p>}
                        <FieldInput field={field} value={getFieldValue(payload, field.path)} onChange={(value) => onFieldChange(field.path, value)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!hasContent && <p className='text-muted-foreground text-center py-8 text-sm'>{t('no_content') || 'No variables or form fields available'}</p>}

              {/* Generate Button */}
              <div className='flex gap-3 pt-4 border-t'>
                <Button onClick={handleRender} disabled={rendering || !hasContent || loadingContext}>
                  {rendering ? (
                    <><Loader2 className='mr-2 h-4 w-4 animate-spin' />{t('rendering') || 'Rendering'}...</>
                  ) : (
                    <><FileText className='mr-2 h-4 w-4' />{t('generate_document') || 'Generate Document'}</>
                  )}
                </Button>
              </div>
              {pdfUrl && (<Card className='bg-muted/50'><CardContent className='p-4'><div className='flex items-center justify-between'><div className='flex items-center gap-2'><FileText className='h-5 w-5 text-primary' /><div><p className='font-medium'>{t('document_ready') || 'Document Ready'}</p><p className='text-xs text-muted-foreground'>{t('document_generated') || 'Your document has been generated successfully'}</p></div></div><div className='flex gap-2'><Button variant='outline' size='sm' asChild><a href={pdfUrl} target='_blank' rel='noopener noreferrer'><ExternalLink className='mr-2 h-4 w-4' />{t('view') || 'View'}</a></Button><Button size='sm' asChild><a href={pdfUrl} download>{t('download') || 'Download'}</a></Button></div></div></CardContent></Card>)}
            </CardContent>
          </Card>
        )}
      </Main>
    </>
  )
}